import * as React from "react"

import { cn } from "@/lib/utils"
import { Button } from "./button"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "./dropdown-menu"

export interface DropdownButtonMenuItem {
  label: string
  icon?: React.ReactNode
  onClick: () => void
  variant?: "default" | "destructive"
  disabled?: boolean
}

export interface DropdownButtonMenuProps {
  icon?: React.ReactNode
  label?: string
  items: DropdownButtonMenuItem[]
  ariaLabel: string
  align?: "start" | "center" | "end"
  disabled?: boolean
  stopPropagation?: boolean
  className?: string
  separator?: number | number[]
  size?: "default" | "sm" | "lg"
}

export function DropdownButtonMenu({
  icon,
  label,
  items,
  ariaLabel,
  align = "end",
  disabled = false,
  stopPropagation = false,
  className,
  separator,
  size = "sm",
}: DropdownButtonMenuProps) {
  const [open, setOpen] = React.useState(false)

  const handleClick = (e: React.MouseEvent) => {
    if (stopPropagation) {
      e.stopPropagation()
    }
  }

  const separatorIndices = separator
    ? Array.isArray(separator)
      ? separator
      : [separator]
    : []

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild onClick={handleClick}>
        {label ? (
          <Button
            variant="outline"
            size={size}
            className={className}
            disabled={disabled}
            aria-label={ariaLabel}
          >
            {icon && <span className="mr-1.5">{icon}</span>}
            {label}
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            className={cn("h-8 w-8", className)}
            disabled={disabled}
            aria-label={ariaLabel}
          >
            <span className="sr-only">{ariaLabel}</span>
            {icon}
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} onClick={handleClick}>
        {items.map((item, index) => (
          <React.Fragment key={index}>
            <DropdownMenuItem
              onClick={() => {
                if (!item.disabled) {
                  item.onClick()
                  setOpen(false)
                }
              }}
              className={cn(
                item.variant === "destructive" && "text-red-600"
              )}
              data-disabled={item.disabled || undefined}
            >
              {item.icon && <span className="mr-2">{item.icon}</span>}
              {item.label}
            </DropdownMenuItem>
            {separatorIndices.includes(index) && <DropdownMenuSeparator />}
          </React.Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
